import React, { useEffect, useState } from "react";
import { LANDING_NAV_ITEMS } from "./landingNav";
import { navigatePortal } from "../../lib/navigation";
import { useLocale } from "../../context/LocaleContext";
import { DxlLogo } from "./DxlLogo";

function csrfToken() {
  const meta = document.querySelector('meta[name="csrf-token"]');
  return meta?.getAttribute("content") || "";
}

const PORTAL_SECTIONS = [
  { path: "/portal/overview", labelKey: "nav.overview" },
  { path: "/portal/onboarding", labelKey: "nav.onboarding" },
  { path: "/portal/withdrawals", labelKey: "nav.withdrawals" },
  { path: "/portal/guarantees", labelKey: "nav.guarantees" },
  { path: "/portal/ai-labels", labelKey: "nav.aiLabels" },
  { path: "/portal/emails", labelKey: "nav.emails" },
  { path: "/portal/extension-texts", labelKey: "nav.extensionTexts" },
  { path: "/portal/settings", labelKey: "nav.settings" },
  { path: "/portal/exports", labelKey: "nav.exports" },
  { path: "/portal/billing", labelKey: "nav.billing" },
  { path: "/portal/help", labelKey: "nav.help" }
];

export function LandingTopBar({ hidden = false, variant = "marketing", showSettingsLink = false }) {
  const { t } = useLocale();
  const [menuOpen, setMenuOpen] = useState(false);
  const isPortal = variant === "portal";

  useEffect(() => {
    if (hidden && menuOpen) setMenuOpen(false);
  }, [hidden, menuOpen]);

  const toggleMenu = () => setMenuOpen((open) => !open);
  const closeMenu = () => setMenuOpen(false);

  const headerClass = [
    "landing-header",
    hidden && !isPortal ? "landing-header--hidden" : "",
    isPortal ? "landing-header--portal" : ""
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <header className={headerClass}>
      <a href="/portal" className="landing-header__brand" onClick={closeMenu} aria-label="DXL EU Compliance Suite">
        <DxlLogo className="landing-header__logo" />
        <span className="landing-header__brand-text">EU Compliance Suite</span>
      </a>

      <div className="landing-header__actions">
        {isPortal ? (
          <a href="/portal/help" className="landing-header__cta-pill" onClick={closeMenu}>
            {t("nav.help")}
          </a>
        ) : (
          <a href="/users/sign_up" className="landing-header__cta-pill">
            Create account
          </a>
        )}

        <button
          type="button"
          className="landing-header__menu-btn"
          aria-expanded={menuOpen}
          aria-controls="site-menu"
          onClick={toggleMenu}
        >
          <span className="landing-header__menu-label">{menuOpen ? "Close" : "Menu"}</span>
          <span className="landing-header__menu-icon" aria-hidden="true">
            {menuOpen ? (
              <span className="landing-header__menu-icon-close" />
            ) : (
              <>
                <span />
                <span />
              </>
            )}
          </span>
        </button>
      </div>

      <nav
        id="site-menu"
        className={`landing-header__menu ${menuOpen ? "is-open" : ""}`}
        aria-hidden={!menuOpen}
      >
        <div className="landing-header__menu-panel">
          {isPortal
            ? PORTAL_SECTIONS.map((item) => (
                <a
                  key={item.path}
                  href={item.path}
                  className="landing-header__menu-link"
                  onClick={(event) => {
                    event.preventDefault();
                    closeMenu();
                    navigatePortal(item.path);
                  }}
                >
                  {t(item.labelKey)}
                </a>
              ))
            : [
                ...LANDING_NAV_ITEMS.map((item) => (
                  <a
                    key={item.id}
                    href={`#${item.id}`}
                    className="landing-header__menu-link"
                    onClick={closeMenu}
                  >
                    {item.label}
                  </a>
                )),
                showSettingsLink ? (
                  <a
                    key="portal-settings"
                    href="/portal/settings"
                    className="landing-header__menu-link"
                    onClick={(event) => {
                      event.preventDefault();
                      closeMenu();
                      navigatePortal("/portal/settings");
                    }}
                  >
                    {t("nav.settings")}
                  </a>
                ) : null
              ]}
          <div className="landing-header__menu-divider" />
          {isPortal ? (
            <form method="post" action="/users/sign_out" className="m-0">
              <input type="hidden" name="_method" value="delete" />
              <input type="hidden" name="authenticity_token" value={csrfToken()} />
              <button type="submit" className="landing-header__menu-link landing-header__menu-link--muted w-full text-left bg-transparent border-0 cursor-pointer font-inherit">
                Sign out
              </button>
            </form>
          ) : (
            <a href="/users/sign_in" className="landing-header__menu-link landing-header__menu-link--muted">
              Sign in
            </a>
          )}
        </div>
      </nav>
    </header>
  );
}
